import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import {
    User,
    LayoutDashboard,
    Blocks,
    ShoppingBag,
    Info,
    LogOut,
    Loader2,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    Shield,
    ShieldCheck,
    Users2,
    // PackageCheck,
    FileText,
    Settings,
    ClipboardCheck, // âœ… Tambahkan ikon untuk menu Verifikasi
    FileSearch, // âœ… Tambahkan ikon untuk menu Conference Check
    Building2,
    Sun,
    Moon,
    Globe,
    BadgeCheck,
    PanelLeft,
    Nfc,
    ScanLine,
    CalendarDays,
    PackageSearch,
    Images,
    Users,
    Award,
    PenTool,
    MapPin,
    Mic,
    Tags,
    PartyPopper,
    MessageSquareHeart,
    IdCard,
    GraduationCap,
    BookOpen,
    BookMarked,
    Wallet,
    Receipt,
    Search,
    X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import toast from "react-hot-toast";
import { logout } from "@/services/auth";
import { setLoggingOut } from "@/lib/axiosClient";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next"; // 1. Import i18n
import { useSidebarContext } from "@/components/private/layout/SidebarContext";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface MenuItem {
    title: string;
    path: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    isAction?: boolean;
    group?: string;
}

export default function Sidebar() {
    const { t, i18n } = useTranslation(['navigation', 'common']); // 2. Gunakan namespace
    const location = useLocation();
    const navigate = useNavigate();
    const { data: user } = useUser();
    const { data: activePeriod } = useActivePeriod();
    const { isMobileOpen, setIsMobileOpen, isDesktopCollapsed, toggleDesktopSidebar } = useSidebarContext();

    const [orderLoading, setOrderLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    const participantId = user?.participant_id;
    const periodId = activePeriod?.id;

    // --- Logic Role ---
    const isParticipant = !!participantId;

    // --- Logic Pengecekan Registrasi (Hanya Peserta) ---
    const { data: registrationCheck, isLoading: loadingCheck } = useQuery({
        queryKey: ["registration-check", participantId, periodId],
        queryFn: async () => {
            if (!isParticipant || !periodId) return null;
            const { data } = await axiosClient.get(
                `/participants/${participantId}/registrations/check/${periodId}`
            );
            return data;
        },
        enabled: isParticipant && !!periodId,
    });

    const isRegistered = registrationCheck?.registered ?? false;

    const handleOrderClick = async () => {
        if (!isParticipant) {
            navigate("/admin/packages");
            return;
        }

        if (orderLoading) return;
        setOrderLoading(true);

        try {
            if (!activePeriod) {
                toast.error(t('common:messages.error_no_period') || "Periode aktif belum tersedia.");
                return;
            }
            if (loadingCheck) {
                toast.error(t('navigation:sidebar.loading'));
                return;
            }
            if (registrationCheck && !isRegistered) {
                toast(t('common:messages.no_active_package') || "Anda belum memiliki paket aktif.");
                navigate("/order/paket");
                return;
            }

            const { data: registrationStep } = await axiosClient.get(
                `/participants/${participantId}/registrations/period/${activePeriod.id}`
            );

            const step = registrationStep.data.current_step;
            const stepRoutes: Record<number, string> = {
                1: "/order/paket",
                2: "/order/konfirmasi",
                3: "/order/pembayaran",
                4: "/order/pembayaran/status",
                5: "/order/kartu",
            };

            navigate(stepRoutes[step] ?? "/order/paket", { replace: true });
        } catch (err) {
            console.error("Sidebar handleOrderClick error:", err);
            toast.error(t('common:messages.error_generic'));
        } finally {
            setOrderLoading(false);
        }
    };

    const handleLogout = async () => {
        setLogoutLoading(true);
        setLoggingOut(true);
        const success = await logout();
        setLoggingOut(false);
        setLogoutLoading(false);

        if (success) {
            toast.success(t('common:messages.success_logout') || "Berhasil logout");
            navigate("/login");
        } else toast.error(t('common:messages.error_logout') || "Gagal logout");
    };

    // --- Menu Items ---
    const menuItems: MenuItem[] = useMemo(() => {
        const baseMenu: MenuItem[] = [
            { title: t('navigation:sidebar.menu.dashboard'), path: "/dashboard", icon: LayoutDashboard, group: t('navigation:sidebar.group.general', 'Umum') },
            { title: t('navigation:sidebar.menu.profile'), path: "/profile", icon: User, group: t('navigation:sidebar.group.general', 'Umum') },
        ];

        if (isParticipant) {
            return [
                ...baseMenu,
                { title: t('navigation:sidebar.menu.order'), path: "/order/paket", icon: ShoppingBag, isAction: true, group: t('navigation:sidebar.group.general', 'Umum') },
                { title: t('navigation:sidebar.menu.tracks'), path: "/conference-tracks", icon: Blocks, group: t('navigation:sidebar.group.general', 'Umum') },
                { title: t('navigation:sidebar.menu.about'), path: "/about", icon: Info, group: t('navigation:sidebar.group.others', 'Lainnya') },
            ];
        }

        const nonParticipantMenu = [...baseMenu];

        // Conference Tracks (Hanya untuk Admin / Role 1)
        if (user?.role_id === 1) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.tracks'), path: "/conference-tracks", icon: Blocks, group: t('navigation:sidebar.group.conference', 'Conference') });
        }

        // Verifikasi Conference (Ditampilkan untuk Admin dan Operator / Role 1 & 4)
        if (user?.role_id === 1 || user?.role_id === 4) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.check'), path: "/conference-check", icon: FileSearch, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.verification'), path: "/conference-verification", icon: ClipboardCheck, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.collective_registration'), path: "/collective-billing", icon: Building2, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.conference_rooms', 'Kelola Ruangan'), path: "/admin/conference-rooms", icon: Building2, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.conference_speakers', 'Pembicara Eksternal'), path: "/admin/conference-speakers", icon: Mic, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.mainroom_attendance', 'Presensi Mainroom'), path: "/admin/mainroom-attendance", icon: PartyPopper, group: t('navigation:sidebar.group.conference', 'Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.feedback', 'Feedback Peserta'), path: "/admin/feedback", icon: MessageSquareHeart, group: t('navigation:sidebar.group.conference', 'Conference') });
            }

        if (user?.role_id === 1 || user?.role_id === 4 || user?.role_id === 5) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.attendance', 'Presensi Ruangan'), path: "/conference-attendance", icon: MapPin, group: t('navigation:sidebar.group.conference', 'Conference') });
        }

        // Output Conference (Admin / Role 1 & Operator Output Conference / Role 10 & Operator Conference / Role 4) - diletakkan
        // tepat di bawah Presensi Ruangan, masih satu rumpun menu Conference
        if (user?.role_id === 1 || user?.role_id === 4 || user?.role_id === 10) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_decisions', 'Keputusan Publikasi'), path: "/admin/output-conference/decisions", icon: GraduationCap, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_master_journals', 'Master Jurnal'), path: "/admin/output-conference/master-journals", icon: BookOpen, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_fees', 'Biaya Publikasi'), path: "/admin/output-conference/fees", icon: Wallet, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_billings', 'Tagihan Publikasi'), path: "/admin/output-conference/billings", icon: Receipt, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_publishers', 'Publisher Proceeding'), path: "/admin/output-conference/publishers", icon: BookMarked, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.output_settings', 'Pengaturan Output Conference'), path: "/admin/output-conference/settings", icon: Settings, group: t('navigation:sidebar.group.output_conference', 'Output Conference') });
        }

        // RFID (Admin / Role 1 & Petugas RFID / Role 7)
        if (user?.role_id === 1 || user?.role_id === 7) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.event_guests', 'Panitia & Tamu'), path: "/admin/event-guests", icon: IdCard, group: t('navigation:sidebar.group.convention', 'Konvensi') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.register_rfid', 'Daftarkan RFID'), path: "/admin/rfid/register", icon: Nfc, group: t('navigation:sidebar.group.convention', 'Konvensi') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.check_rfid', 'Cek RFID Scan'), path: "/admin/rfid/scan", icon: ScanLine, group: t('navigation:sidebar.group.convention', 'Konvensi') });
        }

        if (user?.role_id === 1) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.participants_data', 'Data Partisipan'), path: "/admin/participants", icon: Users2, group: t('navigation:sidebar.group.convention', 'Konvensi') });
        }

        // Dashboard Pimpinan (Admin / Role 1 & Pimpinan / Role 8)
        if (user?.role_id === 1 || user?.role_id === 8) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.convention_report', 'Rekap Konvensi'), path: "/admin/convention-report", icon: FileText, group: t('navigation:sidebar.group.convention', 'Konvensi') });
        }

        // Laporan dan Pengaturan (Hanya untuk Admin / Role 1)
        if (user?.role_id === 1) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.certificates', 'Sertifikat'), path: "/admin/certificates", icon: Award, group: t('navigation:sidebar.group.certificate', 'Sertifikat') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.certificate_types', 'Tipe Sertifikat'), path: "/admin/certificate-types", icon: Tags, group: t('navigation:sidebar.group.certificate', 'Sertifikat') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.certificate_templates', 'Template Sertifikat'), path: "/admin/certificate-templates", icon: LayoutDashboard, group: t('navigation:sidebar.group.certificate', 'Sertifikat') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.tte_officials', 'Pengaturan Penandatangan'), path: "/admin/certificates/officials", icon: PenTool, group: t('navigation:sidebar.group.certificate', 'Sertifikat') });

            
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.reports'), path: "/admin/reports", icon: FileText, group: t('navigation:sidebar.group.convention', 'Konvensi') });

            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.manage_packages', 'Kelola Paket'), path: "/admin/packages", icon: PackageSearch, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.manage_documents', 'Kelola Dokumen'), path: "/admin/documents", icon: FileText, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.manage_gallery', 'Kelola Galeri'), path: "/admin/gallery", icon: Images, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.manage_periods', 'Kelola Periode'), path: "/admin/periods", icon: CalendarDays, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.manage_roles', 'Kelola Peran'), path: "/admin/roles", icon: Shield, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.conferences', 'Kelola Conference'), path: "/admin/conferences", icon: CalendarDays, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.users_data', 'Data Pengguna'), path: "/admin/users", icon: Users, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
        }

        // About & Settings (Ditampilkan untuk Admin dan Operator)
        nonParticipantMenu.push({ title: t('navigation:sidebar.menu.about_konaspi'), path: "/about", icon: Info, group: t('navigation:sidebar.group.others', 'Lainnya') });
        if (user?.role_id === 1) {
            nonParticipantMenu.push({ title: t('navigation:sidebar.menu.settings'), path: "/settings/app", icon: Settings, group: t('navigation:sidebar.group.settings', 'Pengaturan') });
        }

        return nonParticipantMenu;
    }, [isParticipant, t, user?.role_id]);

    const groupedMenu = useMemo(() => {
        const result: { group: string; items: MenuItem[] }[] = [];
        menuItems.forEach(item => {
            const g = item.group || t('navigation:sidebar.group.others', 'Lainnya');
            let groupObj = result.find(r => r.group === g);
            if (!groupObj) {
                groupObj = { group: g, items: [] };
                result.push(groupObj);
            }
            groupObj.items.push(item);
        });
        return result;
    }, [menuItems]);

    // --- Pencarian Menu ---
    const [menuSearch, setMenuSearch] = useState("");

    const filteredGroupedMenu = useMemo(() => {
        if (!menuSearch.trim()) return groupedMenu;
        const q = menuSearch.trim().toLowerCase();
        return groupedMenu
            .map(group => ({ ...group, items: group.items.filter(item => item.title.toLowerCase().includes(q)) }))
            .filter(group => group.items.length > 0);
    }, [groupedMenu, menuSearch]);

    // --- Collapsible State Management ---
    // Semua grup (bukan cuma "Pengaturan") sekarang bisa di-collapse, supaya sidebar tidak
    // sepanjang itu lagi meski menunya banyak. Grup "Umum" (Dashboard/Profil) sengaja selalu
    // terbuka karena isinya sedikit & sering diakses.
    const generalGroupName = t('navigation:sidebar.group.general', 'Umum');
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    // Grup berisi route yang sedang aktif otomatis terbuka (mode akordeon - grup lain otomatis
    // tertutup), supaya menu yang dipilih tidak "hilang" tersembunyi di dalam grup yang tertutup.
    useEffect(() => {
        const activeGroup = groupedMenu.find(group => group.items.some(item => location.pathname.startsWith(item.path)));
        if (activeGroup) {
            setOpenGroups(prev => (prev[activeGroup.group] && Object.keys(prev).length === 1) ? prev : { [activeGroup.group]: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, groupedMenu]);

    // Mode akordeon: buka 1 grup otomatis menutup grup lainnya, supaya sidebar tetap ringkas
    // (tidak numpuk banyak grup terbuka sekaligus).
    // Ref per grup (dibedakan desktop/mobile karena renderSidebarContent dipanggil 2x) - dipakai
    // untuk auto-scroll ke grup yang baru dibuka, supaya headernya langsung kelihatan tanpa user
    // harus cari-cari/scroll manual sendiri.
    const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    const toggleGroup = (groupName: string, isOpen: boolean, refKey?: string) => {
        setOpenGroups(isOpen ? { [groupName]: true } : {});

        if (isOpen && refKey) {
            // Tunggu 1 frame + animasi buka Collapsible mulai jalan, baru scroll - supaya posisi
            // akhirnya pas (kalau langsung scroll, tinggi konten yang dituju belum final).
            requestAnimationFrame(() => {
                setTimeout(() => {
                    groupRefs.current.get(refKey)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 120);
            });
        }
    };

    // Ikon representatif per grup, dipakai di header Collapsible.
    const groupIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = useMemo(() => ({
        [t('navigation:sidebar.group.general', 'Umum')]: LayoutDashboard,
        [t('navigation:sidebar.group.conference', 'Conference')]: Building2,
        [t('navigation:sidebar.group.convention', 'Konvensi')]: IdCard,
        [t('navigation:sidebar.group.certificate', 'Sertifikat')]: Award,
        [t('navigation:sidebar.group.output_conference', 'Output Conference')]: GraduationCap,
        [t('navigation:sidebar.group.settings', 'Pengaturan')]: Settings,
        [t('navigation:sidebar.group.others', 'Lainnya')]: Info,
    }), [t]);

    const renderSidebarContent = (isMobile = false) => (
        <div className="h-full flex flex-col justify-between overflow-hidden">
            {/* --- 1. Header Profile (ringkas, 1 baris utama + badge kecil) --- */}
            <div className="pt-3 pb-2.5 px-4 border-b border-gray-100 dark:border-gray-800/80 shrink-0 relative overflow-hidden group bg-gradient-to-b from-slate-50/50 via-white to-transparent dark:from-slate-900/50 dark:via-gray-900 dark:to-transparent">
                {/* Subtle Decorative Glow di Background Header */}
                <div className={cn(
                    "absolute -right-8 -top-8 w-20 h-20 rounded-full blur-2xl opacity-15 pointer-events-none transition-all group-hover:opacity-25",
                    isParticipant ? "bg-primary" : "bg-blue-600"
                )} />

                <div className="flex items-center justify-between gap-2 relative z-10">
                    <div className="flex items-center gap-2.5 min-w-0">
                        {/* Avatar Modern dengan Online Indicator */}
                        <div className="relative shrink-0">
                            <div className={cn(
                                "h-9 w-9 rounded-xl flex items-center justify-center text-white font-extrabold text-sm shadow-md transition-transform duration-300 group-hover:scale-105",
                                isParticipant ? "bg-gradient-to-br from-primary to-blue-600 shadow-primary/20" : "bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/20"
                            )}>
                                {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 border-2 border-white dark:border-gray-900" title={t('navigation:sidebar.online_status', 'Online & Aktif')}></span>
                            </span>
                        </div>

                        <div className="text-left min-w-0">
                            <h3 className="font-extrabold text-sm text-gray-900 dark:text-white truncate leading-tight tracking-tight" title={user?.email || undefined}>
                                {user?.name || t('navigation:sidebar.default_participant_name', 'Peserta Konaspi')}
                            </h3>
                            <div className="flex items-center gap-1 mt-0.5">
                                <span className={cn(
                                    "inline-flex items-center gap-1 px-1.5 py-[1px] rounded-md text-[9px] font-bold tracking-wide",
                                    isParticipant
                                        ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground"
                                        : "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400"
                                )}>
                                    <ShieldCheck className="w-2.5 h-2.5 shrink-0" />
                                    {isParticipant
                                        ? t('navigation:sidebar.participant', 'Peserta')
                                        : (user?.role_id === 4 ? t('navigation:sidebar.operator', 'Operator') : t('navigation:sidebar.administrator', 'Administrator'))}
                                </span>
                                {user?.is_verified && (
                                    <span title={t('navigation:sidebar.verified', 'Terverifikasi')}>
                                        <BadgeCheck className="w-3 h-3 shrink-0 text-emerald-500" />
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {!isMobile && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleDesktopSidebar()}
                            className="h-7 w-7 rounded-lg text-gray-500 hover:!bg-slate-100 dark:hover:!bg-slate-800 hover:!text-slate-900 dark:text-gray-400 dark:hover:!text-white transition-all duration-200 shrink-0 group/btn cursor-pointer"
                            title={t('navigation:sidebar.close_sidebar', 'Tutup Sidebar')}
                        >
                            <ChevronLeft className="h-4 w-4 transition-transform group-hover/btn:-translate-x-0.5" />
                        </Button>
                    )}
                </div>
            </div>

            {/* --- Pencarian Menu --- */}
            <div className="px-3.5 pt-3 pb-1 shrink-0">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                        value={menuSearch}
                        onChange={(e) => setMenuSearch(e.target.value)}
                        placeholder={t('navigation:sidebar.search_menu', 'Cari menu...')}
                        className="h-9 pl-9 pr-8 text-sm rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border-transparent focus-visible:bg-white dark:focus-visible:bg-slate-900"
                    />
                    {menuSearch && (
                        <button
                            type="button"
                            onClick={() => setMenuSearch("")}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Scroll internal tetap diperlukan (bukan dihapus) - sidebar ini "sticky", bukan
                "fixed", jadi kalau kontennya lebih tinggi dari layar dan tidak ada overflow-y-auto
                di sini, bagian bawah sidebar ikut ter-scroll keluar layar bareng halaman utama dan
                jadi tidak terjangkau tanpa scroll halaman. Dengan grup collapse, scroll ini kini
                jarang benar-benar dibutuhkan karena kontennya sudah jauh lebih pendek dari sebelumnya. */}
            <div className="px-3.5 py-3 flex-1 overflow-y-auto space-y-2">
                {filteredGroupedMenu.length === 0 && (
                    <p className="text-center text-xs text-muted-foreground py-6">
                        {t('navigation:sidebar.search_menu_empty', 'Menu tidak ditemukan.')}
                    </p>
                )}
                {filteredGroupedMenu.map((group, groupIndex) => {
                    // Grup "Umum" (Dashboard/Profil) tetap flat - isinya sedikit & paling sering
                    // dipakai, tidak perlu di-collapse. Grup lain semua bisa di-collapse supaya
                    // sidebar tidak panjang meski menunya banyak.
                    const isCollapsibleGroup = group.group !== generalGroupName;
                    const GroupIcon = groupIcons[group.group] ?? Info;

                    const renderGroupItems = () => (
                        <div className="space-y-1">
                            {group.items.map((item, index) => {
                                const isActive = item.isAction
                                    ? location.pathname.startsWith("/order")
                                    : (item.path === "/admin/certificates"
                                        ? location.pathname === item.path
                                        : location.pathname.startsWith(item.path));

                                const Icon = item.icon;

                                return (
                                    <Button
                                        key={index}
                                        variant="ghost"
                                        onClick={() => {
                                            if (isMobile) {
                                                setIsMobileOpen(false);
                                            }
                                            if (item.isAction) {
                                                handleOrderClick();
                                            } else {
                                                navigate(item.path);
                                            }
                                        }}
                                        disabled={item.isAction && orderLoading}
                                        className={cn(
                                            "w-full justify-start h-10.5 mb-1 px-3 transition-all duration-200 relative overflow-hidden rounded-xl group/item cursor-pointer font-medium text-sm",
                                            isActive
                                                ? "!bg-[#003DA5] dark:!bg-blue-600 !text-white dark:!text-white font-bold shadow-md shadow-blue-600/25"
                                                : "text-gray-600 dark:text-gray-300 hover:!bg-slate-100 dark:hover:!bg-slate-800 hover:!text-slate-900 dark:hover:!text-white"
                                        )}
                                    >
                                        <div className="flex items-center w-full min-w-0">
                                            {item.isAction && (orderLoading || loadingCheck) ? (
                                                <Loader2 className={cn("w-4.5 h-4.5 mr-3 animate-spin shrink-0", isActive ? "text-white" : "text-primary")} />
                                            ) : (
                                                <Icon className={cn(
                                                    "w-4.5 h-4.5 mr-3 transition-transform duration-200 group-hover/item:scale-110 shrink-0",
                                                    isActive ? "text-white" : "text-gray-500 dark:text-gray-400 group-hover/item:text-primary"
                                                )} />
                                            )}

                                            <span className="flex-1 text-left truncate">
                                                {item.isAction && (orderLoading || loadingCheck) ? t('navigation:sidebar.loading') : item.title}
                                            </span>

                                            {isActive && !item.isAction ? (
                                                <ChevronRight className="w-4 h-4 text-white/80 ml-auto shrink-0" />
                                            ) : !item.isAction ? (
                                                <ChevronRight className="w-4 h-4 opacity-0 group-hover/item:opacity-40 transition-all -translate-x-1 group-hover/item:translate-x-0 ml-auto shrink-0" />
                                            ) : null}
                                        </div>
                                    </Button>
                                );
                            })}
                        </div>
                    );

                    if (isCollapsibleGroup) {
                        const isGroupActive = group.items.some(item => location.pathname.startsWith(item.path));
                        const isOpen = menuSearch.trim() ? true : (openGroups[group.group] || false);
                        const refKey = `${isMobile}-${group.group}`;

                        return (
                            <Collapsible
                                key={groupIndex}
                                ref={(el) => {
                                    if (el) groupRefs.current.set(refKey, el);
                                    else groupRefs.current.delete(refKey);
                                }}
                                className="mb-2 mt-2 group/collapsible"
                                open={isOpen}
                                onOpenChange={(open) => toggleGroup(group.group, open, refKey)}
                            >
                                <CollapsibleTrigger className={cn(
                                    "flex items-center justify-between w-full h-11 mb-1 px-3 transition-all duration-200 rounded-xl cursor-pointer font-medium text-sm border",
                                    isGroupActive || isOpen
                                        ? "bg-[#003DA5]/5 border-[#003DA5]/10 text-[#003DA5] dark:bg-blue-900/20 dark:border-blue-800/30 dark:text-blue-400 shadow-sm"
                                        : "bg-transparent border-transparent text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                                )}>
                                    <div className="flex items-center min-w-0">
                                        <GroupIcon className={cn(
                                            "w-4.5 h-4.5 mr-3 shrink-0 transition-transform duration-200 group-hover/collapsible:rotate-6",
                                            (isGroupActive || isOpen) ? "text-[#003DA5] dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
                                        )} />
                                        <span className="font-bold truncate">{group.group}</span>
                                        <span className={cn(
                                            "ml-2 shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md",
                                            (isGroupActive || isOpen) ? "bg-[#003DA5]/10 text-[#003DA5] dark:bg-blue-500/20 dark:text-blue-400" : "bg-slate-100 text-gray-400 dark:bg-slate-800 dark:text-gray-500"
                                        )}>
                                            {group.items.length}
                                        </span>
                                    </div>
                                    <ChevronDown className={cn(
                                        "w-4 h-4 transition-transform duration-300 shrink-0",
                                        isOpen ? "rotate-180 text-[#003DA5] dark:text-blue-400" : "text-muted-foreground"
                                    )} />
                                </CollapsibleTrigger>
                                <CollapsibleContent className="overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                                    <div className="pl-4 ml-5 mt-2 mb-2 border-l-2 border-[#003DA5]/20 dark:border-blue-800/40 space-y-1">
                                        {group.items.map((item, index) => {
                                            const isActive = item.isAction
                                                ? location.pathname.startsWith("/order")
                                                : (item.path === "/admin/certificates"
                                                    ? location.pathname === item.path
                                                    : location.pathname.startsWith(item.path));
                                            const Icon = item.icon;
                                            return (
                                                <Button
                                                    key={index}
                                                    variant="ghost"
                                                    onClick={() => {
                                                        if (isMobile) setIsMobileOpen(false);
                                                        if (item.isAction) {
                                                            handleOrderClick();
                                                        } else {
                                                            navigate(item.path);
                                                        }
                                                    }}
                                                    disabled={item.isAction && orderLoading}
                                                    className={cn(
                                                        "w-full justify-start h-10 px-3 transition-all duration-200 relative overflow-hidden rounded-xl group/subitem cursor-pointer text-[13px]",
                                                        isActive
                                                            ? "!bg-[#003DA5] dark:!bg-blue-600 !text-white dark:!text-white font-bold shadow-md shadow-blue-600/25"
                                                            : "text-gray-500 dark:text-gray-400 hover:!bg-slate-100 dark:hover:!bg-slate-800 hover:!text-slate-900 dark:hover:!text-white font-medium"
                                                    )}
                                                >
                                                    <div className="flex items-center w-full min-w-0">
                                                        {item.isAction && (orderLoading || loadingCheck) ? (
                                                            <Loader2 className={cn("w-4 h-4 mr-3 animate-spin shrink-0", isActive ? "text-white" : "text-primary")} />
                                                        ) : (
                                                            <Icon className={cn(
                                                                "w-4 h-4 mr-3 transition-transform duration-200 group-hover/subitem:scale-110 shrink-0",
                                                                isActive ? "text-white" : "text-gray-400 group-hover/subitem:text-[#003DA5] dark:group-hover/subitem:text-blue-400"
                                                            )} />
                                                        )}
                                                        <span className="flex-1 text-left truncate">
                                                            {item.isAction && (orderLoading || loadingCheck) ? t('navigation:sidebar.loading') : item.title}
                                                        </span>
                                                        {isActive && <ChevronRight className="w-3.5 h-3.5 text-white/80 ml-auto shrink-0" />}
                                                    </div>
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    }

                    return (
                        <div key={groupIndex} className="mb-4 mt-2">
                            <div className="px-2.5 mb-2 mt-1 text-left">
                                <p className="text-[10px] font-extrabold text-muted-foreground uppercase tracking-widest">
                                    {group.group}
                                </p>
                            </div>
                            {renderGroupItems()}
                        </div>
                    );
                })}

                {isMobile && (
                    <div className="pt-2 pb-1 space-y-1 border-t border-gray-100 dark:border-gray-800">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                const newLanguage = i18n.language === "id" ? "en" : "id";
                                i18n.changeLanguage(newLanguage);
                            }}
                            className="w-full justify-start h-10 text-gray-600 dark:text-gray-300 hover:!bg-slate-100 dark:hover:!bg-slate-800 hover:!text-slate-900 dark:hover:!text-white rounded-lg text-sm"
                        >
                            <Globe className="w-5 h-5 mr-3 text-gray-500 shrink-0" />
                            <span>{i18n.language === "id" ? "Switch to English" : "Ubah ke Bahasa Indonesia"}</span>
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                const isDarkNow = document.documentElement.classList.contains("dark");
                                if (isDarkNow) {
                                    document.documentElement.classList.remove("dark");
                                    localStorage.setItem("theme", "light");
                                } else {
                                    document.documentElement.classList.add("dark");
                                    localStorage.setItem("theme", "dark");
                                }
                            }}
                            className="w-full justify-start h-10 text-gray-600 dark:text-gray-300 hover:!bg-slate-100 dark:hover:!bg-slate-800 hover:!text-slate-900 dark:hover:!text-white rounded-lg text-sm"
                        >
                            {document.documentElement.classList.contains("dark") ? (
                                <Sun className="w-5 h-5 mr-3 text-gray-500 shrink-0" />
                            ) : (
                                <Moon className="w-5 h-5 mr-3 text-gray-500 shrink-0" />
                            )}
                            <span>{document.documentElement.classList.contains("dark") ? t('navigation:sidebar.menu.is_light_mode', 'Mode Terang') : t('navigation:sidebar.menu.is_dark_mode', 'Mode Gelap')}</span>
                        </Button>
                    </div>
                )}
            </div>

            <div className="px-5 my-1 shrink-0">
                <Separator className="bg-gray-100 dark:bg-gray-800" />
            </div>

            <div className="p-3 mt-auto shrink-0">
                <Button
                    variant="ghost"
                    onClick={() => {
                        if (isMobile) setIsMobileOpen(false);
                        handleLogout();
                    }}
                    disabled={logoutLoading}
                    className="w-full justify-start h-11 text-red-600 dark:text-red-400 hover:!bg-red-50 hover:!text-red-700 dark:hover:!bg-red-950/40 dark:hover:!text-red-300 transition-colors rounded-lg"
                >
                    {logoutLoading ? (
                        <Loader2 className="w-5 h-5 mr-3 animate-spin shrink-0" />
                    ) : (
                        <LogOut className="w-5 h-5 mr-3 shrink-0" />
                    )}
                    <span className="font-medium truncate">
                        {logoutLoading ? t('navigation:sidebar.loading') : t('common:buttons.logout')}
                    </span>
                </Button>

                {activePeriod && (
                    <div className="text-center mt-3 mb-1 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 py-2.5 px-3 rounded-xl border border-primary/15 shadow-2xs">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                            {t('navigation:sidebar.active_period', 'Periode Aktif')}
                        </p>
                        <p className="text-xs font-extrabold text-primary truncate mt-0.5">
                            {activePeriod.name}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <aside className={cn(
                "hidden lg:block lg:col-span-1 mb-6 transition-all duration-300",
                isDesktopCollapsed && "lg:hidden"
            )}>
                <Card className="h-full py-0 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] border border-gray-100 dark:border-gray-800 shadow-xl bg-white/95 backdrop-blur-xl dark:bg-gray-900/95 overflow-hidden flex flex-col rounded-2xl transition-all duration-300">
                    {renderSidebarContent(false)}
                </Card>
            </aside>

            {isDesktopCollapsed && (
                <div className="hidden lg:block fixed left-0 top-28 z-[100] animate-in slide-in-from-left duration-300">
                    <button
                        onClick={() => toggleDesktopSidebar()}
                        className="group flex items-center gap-2.5 py-3.5 px-3.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-l-0 border-gray-200 dark:border-gray-700 shadow-2xl rounded-r-2xl text-primary hover:!bg-[#003DA5] hover:!text-white dark:hover:!bg-blue-600 dark:hover:!text-white transition-all duration-300 cursor-pointer"
                        title={t('navigation:sidebar.reopen_sidebar_title', 'Klik untuk memunculkan kembali Sidebar Navigasi')}
                    >
                        <div className="relative flex items-center justify-center">
                            <PanelLeft className="w-5 h-5 transition-transform group-hover:scale-110 shrink-0" />
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white dark:border-gray-900 rounded-full" />
                        </div>
                        <span className="text-xs font-bold tracking-wide overflow-hidden max-w-0 group-hover:max-w-[150px] transition-all duration-300 ease-out whitespace-nowrap pl-0 group-hover:pl-1">
                            {t('navigation:sidebar.open_sidebar', 'Buka Sidebar')}
                        </span>
                        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1 shrink-0 opacity-70 group-hover:opacity-100" />
                    </button>
                </div>
            )}

            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
                <SheetContent side="left" className="w-[280px] sm:max-w-[300px] p-0 flex flex-col bg-white dark:bg-gray-900 border-r dark:border-gray-800 z-[99999] shadow-2xl">
                    <SheetHeader className="sr-only">
                        <SheetTitle>{t('navigation:sidebar.menu_group', 'Menu Navigasi')}</SheetTitle>
                    </SheetHeader>
                    {renderSidebarContent(true)}
                </SheetContent>
            </Sheet>
        </>
    );
}



